import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import { cva } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils";
import { Label } from "../../base/label/label";
import { Separator } from "../../base/separator/separator";
function FieldSet({ className, ...props }) {
    return /*#__PURE__*/ _jsx("fieldset", {
        "data-slot": "field-set",
        className: cn("flex flex-col gap-6", "has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3", className),
        ...props
    });
}
function FieldLegend({ className, variant = "legend", ...props }) {
    return /*#__PURE__*/ _jsx("legend", {
        "data-slot": "field-legend",
        "data-variant": variant,
        className: cn("mb-3 font-medium", "data-[variant=legend]:text-base", "data-[variant=label]:text-sm", className),
        ...props
    });
}
function FieldGroup({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "field-group",
        className: cn("group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 *:data-[slot=field-group]:gap-4", className),
        ...props
    });
}
const fieldVariants = cva("group/field data-[invalid=true]:text-destructive flex w-full gap-3", {
    variants: {
        orientation: {
            vertical: [
                "flex-col *:w-full [&>.sr-only]:w-auto"
            ],
            horizontal: [
                "flex-row items-center",
                "*:data-[slot=field-label]:flex-auto",
                "has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px has-[>[data-slot=field-content]]:items-start"
            ],
            responsive: [
                "@md/field-group:flex-row @md/field-group:items-center @md/field-group:*:w-auto flex-col *:w-full [&>.sr-only]:w-auto",
                "@md/field-group:*:data-[slot=field-label]:flex-auto",
                "@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px"
            ]
        }
    },
    defaultVariants: {
        orientation: "vertical"
    }
});
function Field({ className, orientation = "vertical", ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        role: "group",
        "data-slot": "field",
        "data-orientation": orientation,
        className: cn(fieldVariants({
            orientation
        }), className),
        ...props
    });
}
function FieldContent({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "field-content",
        className: cn("group/field-content flex flex-1 flex-col gap-1.5 leading-snug", className),
        ...props
    });
}
function FieldLabel({ className, ...props }) {
    return /*#__PURE__*/ _jsx(Label, {
        "data-slot": "field-label",
        className: cn("group/field-label peer/field-label flex w-fit leading-snug group-data-[disabled=true]/field:opacity-50", "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border *:data-[slot=field]:p-4", "has-data-[state=checked]:bg-primary/5 has-data-[state=checked]:border-primary dark:has-data-[state=checked]:bg-primary/10", className),
        ...props
    });
}
function FieldTitle({ className, ...props }) {
    return /*#__PURE__*/ _jsx("div", {
        "data-slot": "field-label",
        className: cn("flex w-fit items-center gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50", className),
        ...props
    });
}
function FieldDescription({ className, ...props }) {
    return /*#__PURE__*/ _jsx("p", {
        "data-slot": "field-description",
        className: cn("text-muted-foreground text-sm font-normal leading-normal group-has-data-[orientation=horizontal]/field:text-balance", "nth-last-2:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-1.5", "[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4", className),
        ...props
    });
}
function FieldSeparator({ children, className, ...props }) {
    return /*#__PURE__*/ _jsxs("div", {
        "data-slot": "field-separator",
        "data-content": !!children,
        className: cn("relative h-5 text-sm group-data-[variant=outline]/field-group:-mb-2", className),
        ...props,
        children: [
            /*#__PURE__*/ _jsx(Separator, {
                className: "absolute inset-0 top-1/2"
            }),
            children && /*#__PURE__*/ _jsx("span", {
                className: "bg-background text-muted-foreground relative mx-auto block w-fit px-2",
                "data-slot": "field-separator-content",
                children: children
            })
        ]
    });
}
function FieldError({ className, children, errors, ...props }) {
    const { t } = useTranslation();
    const content = useMemo(()=>{
        /**
     * Translates error message if it's a translation key (starts with "errors.")
     * Otherwise returns the message as-is
     */ const translateError = (message)=>{
            if (message.startsWith("errors.")) {
                return t(message);
            }
            return message;
        };
        if (children) {
            return children;
        }
        if (!errors) {
            return null;
        }
        if (errors?.length === 1 && errors[0]?.message) {
            return translateError(errors[0].message);
        }
        return /*#__PURE__*/ _jsx("ul", {
            className: "ml-4 flex list-disc flex-col gap-1",
            children: errors.map((error, index)=>error?.message && /*#__PURE__*/ _jsx("li", {
                    children: translateError(error.message)
                }, index))
        });
    }, [
        children,
        errors,
        t
    ]);
    if (!content) {
        return null;
    }
    return /*#__PURE__*/ _jsx("div", {
        role: "alert",
        "data-slot": "field-error",
        className: cn("text-destructive text-sm font-normal", className),
        ...props,
        children: content
    });
}
export { Field, FieldLabel, FieldDescription, FieldError, FieldGroup, FieldLegend, FieldSeparator, FieldSet, FieldContent, FieldTitle };

//# sourceMappingURL=field.js.map